import { Dayjs } from 'dayjs'
import {
  BaseFormData,
  SavedIndicationGraph,
  SavedWeldIndicationGraph,
} from 'features/reporting/types'
import { GeckoMeasurement } from 'types'
import { z } from 'zod'

import {
  ProbeType,
  SurfaceCondition,
  SurfaceType,
  TechniqueName,
  WaveMode,
} from './sections/Calibration'

const geckoMeasurementSchema = z.custom<GeckoMeasurement>(
  (val) => val instanceof GeckoMeasurement,
  { message: 'Must be a GeckoMeasurement instance' }
)

const dayjsSchema = z.custom<Dayjs>(
  (val) => val && typeof val === 'object' && 'isValid' in val,
  { message: 'Must be a Dayjs instance' }
)

export const mainInfoSchema = z.object({
  asset: z.string().optional(),
  assetId: z.string().optional(),
  customerName: z.string().optional(),
  location: z.string().optional(),
  scopesByScanType: z.record(z.string(), z.string().optional()).optional(),
  surfaceTemperature: z.number().optional(),
  temperatureUnit: z.enum(['Celsius', 'Fahrenheit', 'Ambient']).optional(),
  scanIndicationMode: z.boolean(),
  material: z.string().optional(),
  diameter: geckoMeasurementSchema.optional(),
  length: geckoMeasurementSchema.optional(),
  inspectionSurface: z.custom<SurfaceType>().optional(),
  surfaceCondition: z.custom<SurfaceCondition>().optional(),
  coated: z.boolean().optional(),
})

export const coverPageDataSchema = z.object({
  geckoJobId: z.string().optional(),
  startDate: dayjsSchema.optional(),
  endDate: dayjsSchema.optional(),
})

export const inspectionAreaSchema = z.object({
  name: z.string(),
  minThickness: geckoMeasurementSchema,
  avgThickness: geckoMeasurementSchema,
  nominal: geckoMeasurementSchema,
  scanNumber: z.string(),
})

export const weldEntrySchema = z.object({
  weldType: z.enum(['Circumferential Welds', 'Longseam Welds', 'Nozzle Welds']),
  indicationCount: z.number(),
})

export const inspectionSummaryDataSchema = z.object({
  areas: z.array(inspectionAreaSchema),
  welds: z.array(weldEntrySchema).optional(),
  summary: z.string().optional(),
  disclaimer: z.string(),
  thicknessUnit: z.enum(['mm', 'in']),
})

export const summaryPageDataSchema = z.object({
  inspectionScope: z.string().optional(),
  inspectionSummary: inspectionSummaryDataSchema.optional(),
})

export const reportFormImageDataSchema = z.object({
  imagePath: z.string(),
  imageCaption: z.string().optional(),
})

const calibrationDataFileSchema = z.object({
  originalName: z.string().optional(),
  parseWarnings: z.array(z.string()).optional(),
  uploadWarning: z.string().optional(),
})

const calibrationEntryModeSchema = z.enum(['auto', 'manual'])

const calibrationVerificationSchema = z.object({
  date: dayjsSchema.nullable(),
})

// Angle beam calibration (for PAUT techniques)
const angleBeamCalibrationEntrySchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  distance: geckoMeasurementSchema.optional(),
  amplitude: z.string().optional(),
})

// Per-technique calibration details
const techniqueCalibrationSchema = z.object({
  calBlockType: z.string().optional(),
  calBlockSN: z.string().optional(),
  reflectorType: z.string().optional(),
  reflectorSize: z.string().optional(),

  // 0° Calibration (for Rapid AUT only)
  thinStepDistance: geckoMeasurementSchema.optional(),
  thinStepTolerance: geckoMeasurementSchema.optional(),
  thickStepDistance: geckoMeasurementSchema.optional(),
  thickStepTolerance: geckoMeasurementSchema.optional(),
  verificationStepDistance: geckoMeasurementSchema.optional(),
  verificationStepTolerance: geckoMeasurementSchema.optional(),
})

const inspectionTechniqueSchema = z.object({
  id: z.string().optional(),
  techniqueName: z.custom<TechniqueName>().optional(),
  probeType: z.custom<ProbeType>().optional(),
  probePartNumber: z.string().optional(),
  probeSN: z.string().optional(),
  wedgeType: z.string().optional(),
  waveMode: z.custom<WaveMode>().optional(),
  frequency: z.union([z.number(), z.string()]).optional(),
  elementQuantity: z.number().optional(),
  pitch: geckoMeasurementSchema.optional(),
  angle: z.union([z.number(), z.string()]).optional(),
  scanningGain: z.number().optional(),
  velocity: geckoMeasurementSchema.optional(),
  calibration: techniqueCalibrationSchema.optional(),
})

export const calibrationPageDataSchema = z
  .object({
    calibrationEntryMode: calibrationEntryModeSchema.default('auto'),

    // Procedure information
    procedure: z.string().optional(),
    scanMethods: z.string().optional(),

    // Shared equipment
    testingUnit: z.string().optional(),
    model: z.string().optional(),
    instrumentSN: z.string().optional(),
    instrumentCalibrationDate: dayjsSchema.optional(),
    scannerType: z.string().optional(),
    scannerSN: z.string().optional(),

    // Array of techniques - minimum 1 required
    techniques: z.array(inspectionTechniqueSchema).min(1),

    // Shared motor information
    xMotor: z
      .object({
        function: z.enum(['Index', 'Scanning']).optional(),
        scanRes: geckoMeasurementSchema.optional(),
        speed: geckoMeasurementSchema.optional(),
      })
      .optional(),
    yMotor: z
      .object({
        function: z.enum(['Index', 'Scanning']).optional(),
        scanRes: geckoMeasurementSchema.optional(),
        speed: geckoMeasurementSchema.optional(),
      })
      .optional(),

    // Global angle beam calibration (for PAUT techniques)
    angleBeamCalibration: z.array(angleBeamCalibrationEntrySchema).optional(),

    // Global calibration verification
    calibrationVerification: z.array(calibrationVerificationSchema).optional(),
    calibrationImage: reportFormImageDataSchema.optional(),
    calibrationDataFile: calibrationDataFileSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.calibrationEntryMode !== 'manual' &&
      !data.calibrationDataFile?.originalName
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Calibration data file is required.',
        path: ['calibrationDataFile', 'originalName'],
      })
    }

    if (!data.calibrationImage?.imagePath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Calibration image is required.',
        path: ['calibrationImage', 'imagePath'],
      })
    }
  })

export const clientImageSchema = z.object({
  id: z.string(),
  imagePath: z.string(),
  imageCaption: z.string().optional(),
  type: z.string(),
})

export const clientProvidedImagesDataSchema = z.object({
  images: z.array(clientImageSchema),
})

// Per-indication data within a weld scan
export const weldIndicationSchema = z.object({
  indicationNumber: z.number(),
  start: geckoMeasurementSchema,
  length: geckoMeasurementSchema,
  axialPosition: geckoMeasurementSchema,
  depth: geckoMeasurementSchema,
  height: geckoMeasurementSchema,
  amplitude: z.number(),
  classification: z.string(),
  typeOfIndication: z.string(),
})

export const weldScanSchema = z.object({
  id: z.string(),
  scanLocation: z.string(), // e.x., "CW-1", "CW-2", "LS-1"
  fileName: z.string(), // Immutable file name for backend matching
  scanLabel: z.string(), // ex. S01, S02, etc.

  scanStart: geckoMeasurementSchema,
  scanStop: geckoMeasurementSchema,

  centerlineNeg: geckoMeasurementSchema,
  centerlinePos: geckoMeasurementSchema,

  totalIndications: z.number().min(0).default(0),

  totalLengthOfIndication: geckoMeasurementSchema.optional(),
  indicationType: z.string().optional(),
  comments: z.string().optional(),

  indications: z.array(weldIndicationSchema).default([]),

  imagePath: z.string().optional(),
  imageCaption: z.string().optional(),
})

export const weldScanDataSchema = z.object({
  totalScans: z.number().min(0).default(0),
  scans: z.array(weldScanSchema).default([]),
})

export type MainInfo = z.infer<typeof mainInfoSchema>
export type CoverPageData = z.infer<typeof coverPageDataSchema>
export type InspectionArea = z.infer<typeof inspectionAreaSchema>
export type WeldEntry = z.infer<typeof weldEntrySchema>
export type InspectionSummaryData = z.infer<typeof inspectionSummaryDataSchema>
export type SummaryPageData = z.infer<typeof summaryPageDataSchema>
export type ReportFormImageData = z.infer<typeof reportFormImageDataSchema>
export type CalibrationDataFile = z.infer<typeof calibrationDataFileSchema>
export type CalibrationEntryMode = z.infer<typeof calibrationEntryModeSchema>
export type AngleBeamCalibrationEntry = z.infer<
  typeof angleBeamCalibrationEntrySchema
>
export type TechniqueCalibration = z.infer<typeof techniqueCalibrationSchema>
export type InspectionTechnique = z.infer<typeof inspectionTechniqueSchema>
export type CalibrationPageData = z.infer<typeof calibrationPageDataSchema>
export type ClientImage = z.infer<typeof clientImageSchema>
export type ClientProvidedImagesData = z.infer<
  typeof clientProvidedImagesDataSchema
>
export type WeldIndication = z.infer<typeof weldIndicationSchema>
export type WeldScan = z.infer<typeof weldScanSchema>
export type WeldScanData = z.infer<typeof weldScanDataSchema>

export type WeldData = { weldScanData?: WeldScanData }

export type SummaryFormData = SummaryPageData & MainInfo & WeldData

export interface RautAssetDrawingChunkData {
  bundlePath: string
  chunkKey: string
  descriptorPath?: string
  imagePath: string
  index: number
  legendPath?: string
  scanPlanPath?: string
  type: 'bundle'
}

export interface RautInspectionAssetDrawingsData {
  chunks: Record<string, RautAssetDrawingChunkData>
}

export type RautAssetDrawingsData = Record<
  string,
  RautInspectionAssetDrawingsData
>

export interface RautFormDataState extends BaseFormData {
  mainInfo: MainInfo
  coverPageData: CoverPageData
  summaryPageData: SummaryPageData
  calibrationData: CalibrationPageData
  clientProvidedImagesData: ClientProvidedImagesData
  asset_drawings: RautAssetDrawingsData
  savedIndicationGraphs?: Record<string, SavedIndicationGraph>
  savedWeldIndicationGraphs?: Record<string, SavedWeldIndicationGraph>
  weldScanData?: WeldScanData
}
